import type {
  ChainPlugin,
  SolanaTransactionInput,
  DecodedTransaction,
  ValidationResult,
  ValidationError,
  Warning,
} from '@txviz/core';
import { serializeSolanaTransaction } from './serializer.js';
import { hashSolanaTransaction } from './hasher.js';
import { decodeSolanaTransaction } from './decoder.js';
import { base58Decode } from './types.js';

/**
 * Solana chain plugin.
 *
 * Handles legacy Solana transaction verification.
 *
 * Key difference from Ethereum/Bitcoin: Solana uses Ed25519 which signs
 * the raw message bytes (no hashing step). Verification compares the
 * serialized message bytes directly.
 */
export const solanaPlugin: ChainPlugin<SolanaTransactionInput> = {
  chain: 'solana',

  validateInput(input: SolanaTransactionInput): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: Warning[] = [];

    // Validate fee payer (must be a valid base58 public key = 32 bytes)
    try {
      const feePayerBytes = base58Decode(input.feePayer);
      if (feePayerBytes.length !== 32) {
        errors.push({
          field: 'feePayer',
          message: `Expected 32-byte public key, got ${feePayerBytes.length} bytes`,
        });
      }
    } catch {
      errors.push({ field: 'feePayer', message: 'Invalid base58 public key' });
    }

    // Validate recent blockhash
    try {
      const blockhashBytes = base58Decode(input.recentBlockhash);
      if (blockhashBytes.length !== 32) {
        errors.push({
          field: 'recentBlockhash',
          message: `Expected 32-byte blockhash, got ${blockhashBytes.length} bytes`,
        });
      }
    } catch {
      errors.push({ field: 'recentBlockhash', message: 'Invalid base58 blockhash' });
    }

    // Validate instructions
    if (!input.instructions || input.instructions.length === 0) {
      errors.push({ field: 'instructions', message: 'Transaction must have at least one instruction' });
    }

    for (let i = 0; i < input.instructions.length; i++) {
      const ix = input.instructions[i]!;

      // Validate program ID
      try {
        const programIdBytes = base58Decode(ix.programId);
        if (programIdBytes.length !== 32) {
          errors.push({
            field: `instructions[${i}].programId`,
            message: `Expected 32-byte program ID, got ${programIdBytes.length} bytes`,
          });
        }
      } catch {
        errors.push({
          field: `instructions[${i}].programId`,
          message: 'Invalid base58 program ID',
        });
      }

      // Validate accounts
      for (let j = 0; j < ix.accounts.length; j++) {
        const acc = ix.accounts[j]!;
        try {
          const accBytes = base58Decode(acc.pubkey);
          if (accBytes.length !== 32) {
            errors.push({
              field: `instructions[${i}].accounts[${j}].pubkey`,
              message: `Expected 32-byte public key, got ${accBytes.length} bytes`,
            });
          }
        } catch {
          errors.push({
            field: `instructions[${i}].accounts[${j}].pubkey`,
            message: 'Invalid base58 public key',
          });
        }
      }
    }

    // Size warning
    if (input.instructions.length > 5) {
      warnings.push({
        message: `Transaction has ${input.instructions.length} instructions — review each carefully`,
        severity: 'warning',
      });
    }

    return { valid: errors.length === 0, errors, warnings };
  },

  serialize(input: SolanaTransactionInput): Uint8Array {
    return serializeSolanaTransaction(input);
  },

  hash(serialized: Uint8Array): Uint8Array {
    return hashSolanaTransaction(serialized);
  },

  async decode(input: SolanaTransactionInput): Promise<DecodedTransaction> {
    return decodeSolanaTransaction(input);
  },
};
